import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { RBACProvider } from "@/contexts/RBACContext";
import AppLayout from "@/components/layout/AppLayout";
import ModuleGuard from "@/components/ModuleGuard";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import GoogleCallback from "@/pages/GoogleCallback";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Repository from "@/pages/Repository";
import ContractDetail from "@/pages/ContractDetail";
import ContractStudio from "@/pages/ContractStudio";
import SignatoryExperience from "@/pages/SignatoryExperience";
import BusinessImpact from "@/pages/BusinessImpact";
import Monitoring from "@/pages/Monitoring";
import Workflows from "@/pages/Workflows";
import WorkflowDesigner from "@/pages/WorkflowDesigner";
import WorkflowInstance from "@/pages/WorkflowInstance";
import Counterparty from "@/pages/Counterparty";
import Integrations from "@/pages/Integrations";
import Analytics from "@/pages/Analytics";
import Settings from "@/pages/Settings";
import Team from "@/pages/Team";
import Subscription from "@/pages/Subscription";
import Compliance from "@/pages/Compliance";
import SSO from "@/pages/SSO";
import Boilerplates from "@/pages/Boilerplates";
import Branding from "@/pages/Branding";
import GlobalAdmin from "@/pages/GlobalAdmin";
import RolesPermissions from "@/pages/RolesPermissions";
import DocTypeWorkflows from "@/pages/DocTypeWorkflows";
import StarterPacks from "@/pages/StarterPacks";
import ChangePassword from "@/pages/ChangePassword";
import Inbox, { InboxConsume } from "@/pages/Inbox";
import PublicBriefing from "@/pages/PublicBriefing";
import ClientSummary from "@/pages/ClientSummary";
import LocaleDetectedBanner from "@/components/LocaleDetectedBanner";

function RequireAuth({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" replace />;
  // If the tenant admin (or Clariva global admin) seeded this account with a temporary
  // password, force the user through /change-password before they can reach anything else.
  if (user.force_password_reset && location.pathname !== "/change-password") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/change-password?next=${next}`} replace />;
  }
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <I18nProvider>
        <BrowserRouter>
          <AuthProvider>
            <RBACProvider>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/google/callback" element={<GoogleCallback />} />
              <Route path="/change-password" element={<RequireAuth><ChangePassword /></RequireAuth>} />
              <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
              <Route path="/sign/:id" element={<RequireAuth><SignatoryExperience /></RequireAuth>} />
              <Route path="/briefing/:token" element={<PublicBriefing />} />
              <Route path="/client-summary" element={<ClientSummary />} />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/inbox/consume" element={<InboxConsume />} />
              <Route path="/inbox/:slug" element={<Inbox />} />

              <Route path="/app" element={<RequireAuth><AppLayout /></RequireAuth>}>
                <Route index element={<ModuleGuard module="dashboard"><Dashboard /></ModuleGuard>} />
                <Route path="studio" element={<ModuleGuard module="studio"><ContractStudio /></ModuleGuard>} />
                <Route path="repository" element={<ModuleGuard module="repository"><Repository /></ModuleGuard>} />
                <Route path="repository/:id" element={<ModuleGuard module="repository"><ContractDetail /></ModuleGuard>} />
                <Route path="workflows" element={<ModuleGuard module="workflows"><Workflows /></ModuleGuard>} />
                <Route path="workflows/designer/:id" element={<ModuleGuard module="workflows"><WorkflowDesigner /></ModuleGuard>} />
                <Route path="workflows/instances/:id" element={<ModuleGuard module="workflows"><WorkflowInstance /></ModuleGuard>} />
                <Route path="impact" element={<ModuleGuard module="bir"><BusinessImpact /></ModuleGuard>} />
                <Route path="impact/:id" element={<ModuleGuard module="bir"><BusinessImpact /></ModuleGuard>} />
                <Route path="monitoring" element={<ModuleGuard module="monitoring"><Monitoring /></ModuleGuard>} />
                <Route path="counterparty" element={<ModuleGuard module="counterparty"><Counterparty /></ModuleGuard>} />
                <Route path="integrations" element={<ModuleGuard module="integrations"><Integrations /></ModuleGuard>} />
                <Route path="analytics" element={<ModuleGuard module="analytics"><Analytics /></ModuleGuard>} />
                <Route path="team" element={<ModuleGuard module="team"><Team /></ModuleGuard>} />
                <Route path="roles-permissions" element={<ModuleGuard module="roles_permissions"><RolesPermissions /></ModuleGuard>} />
                <Route path="doc-types" element={<ModuleGuard module="doc_types"><DocTypeWorkflows /></ModuleGuard>} />
                <Route path="starter-packs" element={<ModuleGuard module="starter_packs"><StarterPacks /></ModuleGuard>} />
                <Route path="subscription" element={<ModuleGuard module="subscription"><Subscription /></ModuleGuard>} />
                <Route path="compliance" element={<ModuleGuard module="compliance"><Compliance /></ModuleGuard>} />
                <Route path="sso" element={<ModuleGuard module="integrations"><SSO /></ModuleGuard>} />
                <Route path="boilerplates" element={<ModuleGuard module="boilerplates"><Boilerplates /></ModuleGuard>} />
                <Route path="branding" element={<ModuleGuard module="branding"><Branding /></ModuleGuard>} />
                <Route path="global-admin" element={<ModuleGuard module="global_admin"><GlobalAdmin /></ModuleGuard>} />
                <Route path="settings" element={<ModuleGuard module="team"><Settings /></ModuleGuard>} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster richColors position="top-right" theme="dark" />
            <LocaleDetectedBanner />
            </RBACProvider>
          </AuthProvider>
        </BrowserRouter>
      </I18nProvider>
    </ThemeProvider>
  );
}

export default App;
